import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { getAllAgents } from '../../api/userApi';
import { useUser } from '../../context/userContext';

const FONT = "'Poppins', sans-serif";

const PIE_COLORS = [
  '#1A8CFF', '#F59E0B', '#10B981', '#6366F1', '#EF4444',
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#3B82F6',
];

const STATUS_COLORS = { Active: '#10B981', Archived: '#94A3B8', Inactive: '#EF4444' };

// System accounts to exclude from counts
const SYSTEM_USERNAMES = ['webbey', 'admin'];

function isSystemUser(agent) {
  const uname = (agent.username ?? '').toLowerCase();
  return SYSTEM_USERNAMES.some(s => uname.includes(s)) && !agent.groupName;
}

export default function AgentReport() {
  const { user } = useUser();
  const hasAccess = user?.roleRights?.includes('UserManagement') || user?.roleRights?.includes('AllSalesList');

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hasAccess) return;
    (async () => {
      try {
        const res = await getAllAgents();
        setAgents(res.data ?? []);
      } catch (err) {
        console.error('Error fetching agents:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [hasAccess]);

  /* ---------- Filtered (exclude system users) ---------- */
  const filtered = useMemo(() => agents.filter(a => !isSystemUser(a)), [agents]);

  /* ---------- Summary metrics ---------- */
  const totalAgents = filtered.length;
  const activeAgents = useMemo(() => filtered.filter(a => a.status === 'Active').length, [filtered]);
  const archivedAgents = useMemo(() => filtered.filter(a => a.status === 'Archived').length, [filtered]);

  const hasCreatedOn = useMemo(() => filtered.some(a => a.createdOn), [filtered]);
  const newThisMonth = useMemo(() => {
    if (!hasCreatedOn) return null;
    const startOfMonth = dayjs().startOf('month');
    return filtered.filter(a => a.createdOn && dayjs(a.createdOn).isAfter(startOfMonth.subtract(1, 'day'))).length;
  }, [filtered, hasCreatedOn]);

  /* ---------- Charts data ---------- */

  // Agents by Group (pie)
  const agentsByGroup = useMemo(() => {
    const map = {};
    filtered.forEach(a => {
      const group = a.groupName || 'Unassigned';
      map[group] = (map[group] ?? 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filtered]);

  // Agents by Role (bar horizontal)
  const agentsByRole = useMemo(() => {
    const map = {};
    filtered.forEach(a => {
      (a.userRoles ?? []).forEach(r => {
        const role = r.roleName ?? 'Unknown';
        map[role] = (map[role] ?? 0) + 1;
      });
    });
    return Object.entries(map).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  }, [filtered]);

  // Status breakdown
  const statusData = useMemo(() => {
    const map = {};
    filtered.forEach(a => {
      const st = a.status || 'Unknown';
      map[st] = (map[st] ?? 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [filtered]);

  // New registrations by month (last 12 months)
  const registrationsByMonth = useMemo(() => {
    if (!hasCreatedOn) return [];
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const m = dayjs().subtract(i, 'month');
      months.push({ key: m.format('YYYY-MM'), label: m.format('MMM YYYY'), count: 0 });
    }
    filtered.forEach(a => {
      if (!a.createdOn) return;
      const key = dayjs(a.createdOn).format('YYYY-MM');
      const bucket = months.find(m => m.key === key);
      if (bucket) bucket.count += 1;
    });
    return months.map(({ label, count }) => ({ name: label, count }));
  }, [filtered, hasCreatedOn]);

  // Commission rate distribution (PSC)
  const commissionDist = useMemo(() => {
    const map = {};
    filtered.forEach(a => {
      const rate = a.personalComm ?? 0;
      const label = `${rate}%`;
      map[label] = (map[label] ?? 0) + 1;
    });
    return Object.entries(map)
      .map(([name, count]) => ({ name, count, sortKey: parseFloat(name) }))
      .sort((a, b) => a.sortKey - b.sortKey);
  }, [filtered]);

  /* ---------- Summary cards config ---------- */
  const summaryCards = [
    { label: 'Total Agents', value: totalAgents, sub: 'All registered agents', borderColor: '#1A8CFF' },
    { label: 'Active Agents', value: activeAgents, sub: 'Currently active', borderColor: '#10B981' },
    { label: 'Archived', value: archivedAgents, sub: 'Archived accounts', borderColor: '#94A3B8' },
    { label: 'New This Month', value: newThisMonth !== null ? newThisMonth : 'N/A', sub: newThisMonth !== null ? dayjs().format('MMMM YYYY') : 'Date not available', borderColor: '#F59E0B' },
  ];

  /* ---------- Render ---------- */
  if (!hasAccess) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: FONT, color: '#64748B' }}>You do not have permission to view this report.</Typography>
      </Box>
    );
  }

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      {/* ===== Header ===== */}
      <Box sx={{ mb: 1 }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: FONT }}>Agent Report</h2>
        <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: FONT }}>
          Agent workforce overview and statistics
        </p>
      </Box>

      {/* ===== Summary Cards ===== */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        {summaryCards.map(card => (
          <Card key={card.label} sx={{ borderLeft: `4px solid ${card.borderColor}`, borderRadius: '14px' }}>
            <CardContent sx={{ p: '14px !important', '&:last-child': { pb: '14px !important' } }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', fontFamily: FONT }}>
                {card.label}
              </Typography>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#0F172A', mt: 0.5, fontFamily: FONT }}>
                {card.value}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mt: 0.25, fontFamily: FONT }}>
                {card.sub}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* ===== Charts Row: Group + Role ===== */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
        {/* Agents by Group (Donut) */}
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>
              Agents by Group
            </Typography>
            {agentsByGroup.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={agentsByGroup}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                    labelLine={false}
                  >
                    {agentsByGroup.map((entry, i) => (
                      <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend formatter={value => (
                    <span style={{ fontSize: '0.8rem', fontFamily: FONT, color: '#374151' }}>{value}</span>
                  )} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No group data available</Typography>
            )}
          </CardContent>
        </Card>

        {/* Agents by Role (Horizontal Bar) */}
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>
              Agents by Role
            </Typography>
            {agentsByRole.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(200, agentsByRole.length * 45)}>
                <BarChart data={agentsByRole} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" allowDecimals={false} style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <YAxis type="category" dataKey="name" width={120} style={{ fontSize: '0.75rem', fontFamily: FONT }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#6366F1" radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No role data available</Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* ===== Agent Status Breakdown ===== */}
      <Box sx={{ mb: 2 }}>
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>
              Agent Status Breakdown
            </Typography>
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={75}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                    labelLine={false}
                  >
                    {statusData.map(entry => (
                      <Cell key={entry.name} fill={STATUS_COLORS[entry.name] ?? '#94A3B8'} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend formatter={value => (
                    <span style={{ fontSize: '0.8rem', fontFamily: FONT, color: '#374151' }}>{value}</span>
                  )} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No status data</Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* ===== New Agent Registrations by Month ===== */}
      <Box sx={{ mb: 2 }}>
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>
              New Agent Registrations by Month
            </Typography>
            {!hasCreatedOn ? (
              <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4, fontFamily: FONT }}>
                Registration date not available
              </Typography>
            ) : registrationsByMonth.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={registrationsByMonth} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <YAxis allowDecimals={false} style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#1A8CFF" radius={[4, 4, 0, 0]} barSize={32} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No registration data</Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* ===== Commission Rate Distribution ===== */}
      <Box sx={{ mb: 2 }}>
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>
              Commission Rate Distribution (PSC)
            </Typography>
            {commissionDist.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={commissionDist} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <YAxis allowDecimals={false} style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#F59E0B" radius={[4, 4, 0, 0]} barSize={40} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No commission data</Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </div>
  );
}
